import React, { useState, useEffect } from 'react';
import api from '../api';
import { 
    FileSignature, History, MapPin, Search, 
    Check, X, Clock, Users, Wrench, ChevronDown, 
    AlertTriangle, ShieldCheck, ClipboardList, PenTool,
    Plus, CheckCircle2 
} from 'lucide-react';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const ContractorLogManager = () => {
    // --- RESPONSIVE STATE ---
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [activeTab, setActiveTab] = useState('new');
    const [historyLogs, setHistoryLogs] = useState([]);
    const [showMapModal, setShowMapModal] = useState(false); 
    
    // --- CONFIG DATA ---
    const [projects, setProjects] = useState([]);
    const [engineers, setEngineers] = useState([]);
    const [myRAMS, setMyRAMS] = useState([]);
    const [availablePermits, setAvailablePermits] = useState([]);
    
    // --- COMPANY & SUBCONTRACTOR STATE ---
    const [myCompanyId, setMyCompanyId] = useState(null);
    const [subcontractors, setSubcontractors] = useState([]);
    const [selectedSubIds, setSelectedSubIds] = useState([]);
    
    // --- LOGIC STATES ---
    const [dynamicSections, setDynamicSections] = useState([]); 
    const [questionAnswers, setQuestionAnswers] = useState({}); 
    const [permitData, setPermitData] = useState({});

    // --- WORKER STATES ---
    const [roster, setRoster] = useState([]); 
    const [selectedWorkers, setSelectedWorkers] = useState([]); 
    const [registerRequired, setRegisterRequired] = useState(false);

    const [formData, setFormData] = useState({
        project: '', 
        date: new Date().toISOString().split('T')[0], 
        shift_hours: '', 
        headcount: '', 
        subcontractor_details: '', 
        responsible_engineer: '', 
        related_rams: '', 
        notes: '', 
        location_detail: '', 
        grid_reference: '', 
        equipment_used: '', 
        company: '' 
    });

    // --- INITIAL DATA LOADING ---
    useEffect(() => {
        const loadData = async () => {
            try {
                // 1. Get User Details First
                const userRes = await api.get('users/me/');
                const myCompName = userRes.data.company;
                
                // Attempt to get ID from various possible backend structures
                let myId = userRes.data.company_id || (userRes.data.company_details ? userRes.data.company_details.id : null);

                setFormData(prev => ({ ...prev, company: myCompName }));
                
                // 2. Fetch All Config Data
                const [projRes, engRes, ramsRes, permitsRes, structRes, rosterRes, compsRes] = await Promise.all([
                    api.get('config/projects/'),
                    api.get('config/engineers/'),
                    api.get('config/my-rams/'),
                    api.get('config/permits/'),
                    api.get('config/daily-log-structure/'),
                    api.get('employees/roster/'),
                    api.get('companies/')
                ]);

                // 3. Fallback: If we still don't have myId, find it by name in companies list
                if (!myId && myCompName) {
                    const found = compsRes.data.find(c => c.name === myCompName);
                    if (found) myId = found.id;
                }
                setMyCompanyId(myId);

                setProjects(projRes.data);
                setEngineers(engRes.data);
                setMyRAMS(ramsRes.data);
                setAvailablePermits(permitsRes.data);
                setDynamicSections(structRes.data.sections || []);
                setRegisterRequired(structRes.data.register_required || false);
                setRoster(rosterRes.data || []);

                // 4. FILTER SUBCONTRACTORS (The Fix)
                // We use String() comparison to ensure types don't cause issues
                const subs = compsRes.data.filter(c => {
                    // Must be a Subcontractor type
                    if (c.type !== 'SUB') return false;
                    // Must NOT be me
                    if (myId && String(c.id) === String(myId)) return false;
                    return true;
                });
                
                setSubcontractors(subs);

            } catch (err) { console.error("Error loading config data", err); }
        };
        loadData();
    }, []);

    useEffect(() => {
        if (activeTab === 'history') {
            api.get('logs/mine/').then(res => setHistoryLogs(res.data)).catch(console.error);
        }
    }, [activeTab]);

    // --- HANDLERS ---

    const handleGridSelection = (gridRef) => {
        setFormData(prev => ({ ...prev, grid_reference: gridRef }));
        setShowMapModal(false);
    };

    const handleSubcontractorChange = async (subId, isChecked, subName) => {
        let newIds;
        if (selectedSubIds.includes(subId)) {
            // Deselecting
            newIds = selectedSubIds.filter(id => id !== subId);
            setRoster(prev => prev.filter(person => person._origin_sub_id !== subId));
            setSelectedWorkers(prev => prev.filter(w => w._origin_sub_id !== subId));
        } else {
            // Selecting
            newIds = [...selectedSubIds, subId];
            try {
                // Fetch subcontractor employees
                const res = await api.get(`employees/roster/?company=${subId}`);
                
                // Tag them so we know where they came from
                const newPeople = res.data.map(p => ({ 
                    ...p, 
                    company_name: subName, 
                    _origin_sub_id: subId 
                }));
                
                setRoster(prev => {
                    const existingIds = new Set(prev.map(p => p.id));
                    // Only add people we don't already have
                    return [...prev, ...newPeople.filter(p => !existingIds.has(p.id))];
                });
            } catch (err) { 
                console.error(err);
                alert("Could not load roster for " + subName); 
            }
        }
        setSelectedSubIds(newIds);
        const names = subcontractors.filter(c => newIds.includes(c.id)).map(c => c.name).join(", ");
        setFormData(prev => ({...prev, subcontractor_details: names}));
    };

    const handleWorkerToggle = (worker) => {
        setSelectedWorkers(prev => {
            const exists = prev.find(w => w.id === worker.id);
            if (exists) return prev.filter(w => w.id !== worker.id);
            
            // 🔥 NAME FIX: Use full_name if available, else name
            const bestName = worker.full_name || worker.name;
            
            return [...prev, { 
                id: worker.id, 
                name: bestName, 
                type: worker.type, 
                company: worker.company_name || 'Me', 
                _origin_sub_id: worker._origin_sub_id 
            }];
        });
    };

    // ... (Rest of handlers remain exactly the same) ...
    const handleTogglePermit = (id, forceState = null) => {
        setPermitData(prev => {
            const newData = { ...prev };
            const shouldAdd = forceState !== null ? forceState : !newData[id];
            if (!shouldAdd) delete newData[id]; 
            else if (!newData[id]) newData[id] = { notes: '', answers: {} };
            return newData;
        });
    };

    const handleQuestionAnswer = (qId, answer, linkedPermitId) => {
        setQuestionAnswers(prev => ({...prev, [qId]: answer}));
        if (linkedPermitId && answer === 'Yes') handleTogglePermit(linkedPermitId, true); 
    };

    const handlePermitAnswerChange = (permitId, questionIndex, value) => {
        setPermitData(prev => ({ ...prev, [permitId]: { ...prev[permitId], answers: { ...prev[permitId].answers, [questionIndex]: value } } }));
    };

    const handlePermitNotesChange = (permitId, text) => {
        setPermitData(prev => ({ ...prev, [permitId]: { ...prev[permitId], notes: text } }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (registerRequired) {
            const enteredHeadcount = parseInt(formData.headcount) || 0;
            if (selectedWorkers.length !== enteredHeadcount) {
                return alert(`⚠️ Headcount Mismatch!\n\nEntered: ${enteredHeadcount}\nSelected in Register: ${selectedWorkers.length}`);
            }
        }

        let extraNotes = "\n\n--- SAFETY QUESTIONS ---\n";
        dynamicSections.forEach(sec => {
            extraNotes += `[${sec.title}]\n`;
            sec.questions.forEach(q => extraNotes += `${q.text}: ${questionAnswers[q.id] || '-'}\n`);
        });

        const formattedPermits = Object.keys(permitData).map(permitId => {
            const data = permitData[permitId];
            const permitType = availablePermits.find(p => p.id === parseInt(permitId));
            let description = "--- CHECKLIST ---\n";
            if (permitType && permitType.questions) {
                permitType.questions.forEach((q, idx) => {
                    const qText = typeof q === 'object' ? q.text : q;
                    const val = data.answers[idx];
                    let displayAns = "NO";
                    if (val === true || val === 'Yes') displayAns = "YES";
                    else if (val === false || val === 'No') displayAns = "NO";
                    else if (val && typeof val === 'string') displayAns = val;
                    description += `• ${qText}: ${displayAns}\n`; 
                });
            }
            if (data.notes) description += `\n--- NOTES ---\n${data.notes}`;
            return { type_id: parseInt(permitId), details: description };
        });

        try {
            await api.post('logs/submit/', { 
                ...formData, 
                subcontractor_count: 0, 
                notes: formData.notes + extraNotes, 
                permits: formattedPermits, 
                worker_manifest: selectedWorkers 
            });
            alert("✅ Log Submitted Successfully!");
            setFormData(prev => ({ ...prev, notes: '', subcontractor_details: '', grid_reference: '', location_detail: '', equipment_used: '', headcount: '' }));
            setPermitData({}); setQuestionAnswers({}); setSelectedWorkers([]); setSelectedSubIds([]);
            setActiveTab('history');
        } catch (err) { alert("Error submitting log: " + JSON.stringify(err.response?.data)); }
    };

    // --- RENDER HELPERS ---
    const RadioPill = ({ label, selected, onClick, color }) => (
        <div onClick={onClick} style={{flex: 1, textAlign: 'center', padding: '10px 5px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, borderRadius: '6px', border: `1px solid ${selected ? color : THEME.border}`, background: selected ? `${color}33` : 'transparent', color: selected ? color : THEME.textSec, transition: 'all 0.2s', userSelect: 'none'}}>{label}</div>
    );

    const renderPermitQuestions = (type) => {
        if (!type.questions) return null;
        return type.questions.map((q, idx) => {
            const qText = typeof q === 'object' ? q.text : q;
            const qType = typeof q === 'object' ? (q.type || 'checkbox') : 'checkbox';
            const showIfId = typeof q === 'object' ? q.showIf : null;
            const showIfCond = (typeof q === 'object' ? q.showIfCondition : 'Yes') || 'Yes'; 

            if (showIfId) {
                const parentIdx = type.questions.findIndex(pq => (typeof pq === 'object' ? pq.id : null) === showIfId);
                const parentAnswer = permitData[type.id]?.answers?.[parentIdx]; 
                const normalizedAnswer = (parentAnswer === true) ? 'Yes' : (parentAnswer === false ? 'No' : parentAnswer);
                if (normalizedAnswer !== showIfCond) return null;
            }

            return (
                <div key={idx} style={{marginBottom:'15px', paddingLeft: showIfId ? '15px' : '0', borderLeft: showIfId ? `2px solid ${THEME.border}` : 'none'}}>
                    {qType === 'text' ? ( 
                        <div><label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'4px'}}>{qText}</label><input type="text" value={permitData[type.id]?.answers?.[idx] || ''} onChange={e => handlePermitAnswerChange(type.id, idx, e.target.value)} style={inputStyle} placeholder="Details..." /></div> 
                    ) : ( 
                        <div>
                            <label style={{display:'block', color:'white', fontSize:'0.9rem', marginBottom:'6px'}}>{qText}</label>
                            <div style={{display:'flex', gap:'10px'}}>
                                {['Yes', 'No'].map(opt => {
                                    const isSelected = permitData[type.id]?.answers?.[idx] === opt;
                                    const activeColor = opt === 'Yes' ? THEME.success : THEME.danger; 
                                    return (
                                        <div key={opt} onClick={() => handlePermitAnswerChange(type.id, idx, opt)} style={{flex: 1, textAlign: 'center', padding: '8px', cursor: 'pointer', fontSize: '0.9rem', borderRadius: '6px', border: `1px solid ${isSelected ? activeColor : THEME.border}`, background: isSelected ? `${activeColor}33` : 'transparent', color: isSelected ? 'white' : THEME.textSec, transition: 'all 0.2s'}}>{opt}</div>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                </div>
            );
        });
    };

    const inputStyle = { width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', fontSize:'0.9rem', boxSizing:'border-box', marginBottom:'10px' };
    const gridStyle = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '15px' };

    return (
        <div className="animate-fade-in">
            <div style={{marginBottom: '20px', display: 'flex', gap: '10px'}}>
                <button onClick={() => setActiveTab('new')} style={{padding:'10px 20px', background: activeTab === 'new' ? THEME.primary : 'transparent', border:`1px solid ${THEME.primary}`, color:'white', borderRadius:'6px', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px'}}><FileSignature size={16}/> New Log</button>
                <button onClick={() => setActiveTab('history')} style={{padding:'10px 20px', background: activeTab === 'history' ? THEME.primary : 'transparent', border:`1px solid ${THEME.primary}`, color:'white', borderRadius:'6px', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px'}}><History size={16}/> History</button>
            </div>

            {/* --- MAP PICKER MODAL --- */}
            {showMapModal && (
                <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px'}}>
                    <div className="card" style={{width: '100%', maxWidth: '900px', height: '80vh', background: THEME.card, padding: '20px', borderRadius: '12px', border: `1px solid ${THEME.border}`, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'15px'}}>
                            <h3 style={{margin:0, color:'white', display:'flex', alignItems:'center', gap:'8px'}}><MapPin size={20} color={THEME.primary}/> Tap Location on Map</h3>
                            <button onClick={() => setShowMapModal(false)} style={{background:'transparent', border:'none', color:THEME.textSec, fontSize:'1.5rem', cursor:'pointer'}}><X size={24}/></button>
                        </div>
                        <div style={{flex: 1, overflow: 'auto', border: `2px dashed ${THEME.border}`, borderRadius: '8px', background: '#020617'}}>
                            <MapPicker onSelect={handleGridSelection} />
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'new' && (
                <div className="card" style={{background: THEME.card, padding: isMobile ? '15px' : '25px', borderRadius:'12px', border:`1px solid ${THEME.border}`, maxWidth:'900px', margin:'0 auto'}}>
                    <h2 style={{color:'white', marginTop:0, display:'flex', alignItems:'center', gap:'10px'}}><FileSignature size={28}/> Submit Log</h2>
                    <form onSubmit={handleSubmit}>
                        {/* ... (Project & Location Section Same as before) ... */}
                        <div style={{marginBottom:'20px', paddingBottom:'20px', borderBottom:`1px dashed ${THEME.border}`}}>
                            <h4 style={{color:THEME.textSec, marginTop:0}}>1. Project & Location</h4>
                            <div style={gridStyle}>
                                <select value={formData.project} onChange={e => setFormData({...formData, project: e.target.value})} style={inputStyle} required>
                                    <option value="">-- Select Project --</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                                <input type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} style={inputStyle} required />
                                <input type="text" placeholder="Location (e.g. Zone B)" value={formData.location_detail} onChange={e => setFormData({...formData, location_detail: e.target.value})} style={inputStyle} required />
                                <div style={{display:'flex', gap:'5px', alignItems:'flex-start'}}>
                                    <input type="text" placeholder="Grid (e.g. A1)" value={formData.grid_reference} onChange={e => setFormData({...formData, grid_reference: e.target.value.toUpperCase()})} style={{...inputStyle, flex:1, marginBottom:0}} />
                                    <button type="button" onClick={() => setShowMapModal(true)} style={{padding: '0 12px', height: '42px', background: THEME.primary, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '1.2rem'}} title="Pick on Map"><MapPin size={20}/></button>
                                </div>
                            </div>
                        </div>

                        <div style={{marginBottom:'20px', paddingBottom:'20px', borderBottom:`1px dashed ${THEME.border}`}}>
                            <h4 style={{color:THEME.textSec, marginTop:0}}>2. People & Shift</h4>
                            <div style={gridStyle}>
                                <div><label style={{color:THEME.textSec, fontSize:'0.8rem'}}>Shift Hours</label><input type="number" step="0.5" placeholder="e.g. 8.0" value={formData.shift_hours} onChange={e => setFormData({...formData, shift_hours: e.target.value})} style={inputStyle} required /></div>
                                <div><label style={{color:THEME.textSec, fontSize:'0.8rem'}}>Total Headcount</label><input type="number" placeholder="Me + Subs" value={formData.headcount} onChange={e => setFormData({...formData, headcount: e.target.value})} style={inputStyle} required /></div>
                            </div>
                            
                            {/* 🔥 THE FILTERED SUB LIST RENDERS HERE */}
                            {subcontractors.length > 0 && (
                                <div style={{marginTop:'15px'}}>
                                    <label style={{color:THEME.textSec, fontSize:'0.9rem', display:'block', marginBottom:'8px'}}>Include Subcontractors:</label>
                                    <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                                        {subcontractors.map(sub => {
                                            const isSel = selectedSubIds.includes(sub.id);
                                            return (
                                                <div key={sub.id} onClick={() => handleSubcontractorChange(sub.id, null, sub.name)} style={{padding:'8px 16px', borderRadius:'20px', cursor:'pointer', fontSize:'0.85rem', userSelect:'none', border: isSel ? `1px solid ${THEME.primary}` : `1px solid ${THEME.border}`, background: isSel ? 'rgba(59, 130, 246, 0.2)' : '#0f172a', color: isSel ? 'white' : THEME.textSec, display:'flex', alignItems:'center', gap:'6px'}}>
                                                    {isSel ? <Check size={14}/> : <Plus size={14}/>} {sub.name}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {registerRequired && (
                                <div style={{marginTop:'20px', background:'#0f172a', padding:'15px', borderRadius:'8px', border:`1px solid ${THEME.border}`}}>
                                    <div style={{display:'flex', justifyContent:'space-between', marginBottom:'10px'}}>
                                        <div style={{color:THEME.warning, fontWeight:'bold', fontSize:'0.9rem', display:'flex', alignItems:'center', gap:'6px'}}><Users size={16}/> Select Workers</div>
                                        <div style={{fontSize:'0.85rem', color: selectedWorkers.length === (parseInt(formData.headcount) || 0) ? THEME.success : THEME.danger}}>Selected: {selectedWorkers.length} / {formData.headcount || 0}</div>
                                    </div>
                                    <div style={{display:'flex', flexWrap:'wrap', gap:'8px', maxHeight:'200px', overflowY:'auto'}}>
                                        {roster.map(p => {
                                            const isSel = selectedWorkers.some(w => w.id === p.id);
                                            return (
                                                <div key={p.id} onClick={() => handleWorkerToggle(p)} style={{padding:'6px 12px', borderRadius:'20px', cursor:'pointer', fontSize:'0.85rem', border: isSel ? `1px solid ${THEME.success}` : `1px solid ${THEME.border}`, background: isSel ? 'rgba(16, 185, 129, 0.2)' : 'transparent', color: isSel ? 'white' : THEME.textSec, userSelect:'none'}}>
                                                    {/* 🔥 RENDER FIX: Shows full_name OR name */}
                                                    {isSel ? '✓' : '+'} {p.full_name || p.name} {p.company_name ? <span style={{opacity:0.6}}>({p.company_name})</span> : ''}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ... (Work & Safety, Questions, Permits, Submit Button same as before) ... */}
                        <div style={{marginBottom:'20px'}}>
                            <h4 style={{color:THEME.textSec, marginTop:0}}>3. Work & Safety</h4>
                            <textarea rows="3" placeholder="Description of work done..." value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} style={{...inputStyle, resize:'vertical'}} required />
                            
                            <div style={gridStyle}>
                                <div style={{zIndex: 20}}> 
                                    <label style={{color:THEME.textSec, fontSize:'0.8rem', display:'block', marginBottom:'5px'}}>Related RAMS</label>
                                    <SearchableDropdown 
                                        options={myRAMS}
                                        value={formData.related_rams}
                                        onChange={(id) => setFormData({...formData, related_rams: id})}
                                        placeholder="-- Search RAMS --"
                                        labelFn={(r) => r.title}
                                    />
                                </div>
                                <div style={{zIndex: 19}}>
                                    <label style={{color:THEME.textSec, fontSize:'0.8rem', display:'block', marginBottom:'5px'}}>Site Engineer</label>
                                    <SearchableDropdown 
                                        options={engineers}
                                        value={formData.responsible_engineer}
                                        onChange={(id) => setFormData({...formData, responsible_engineer: id})}
                                        placeholder="-- Search Engineer --"
                                        labelFn={(eng) => eng.username}
                                    />
                                </div>
                            </div>
                            <input type="text" placeholder="Equipment Used" value={formData.equipment_used} onChange={e => setFormData({...formData, equipment_used: e.target.value})} style={{...inputStyle, marginTop:'15px'}} />
                        </div>
                        
                        {/* Dynamic Sections */}
                        {dynamicSections.map((sec, sIdx) => (
                            <div key={sIdx} style={{marginBottom:'20px', background:'#020617', padding:'15px', borderRadius:'8px', border:`1px solid ${THEME.border}`}}>
                                <h4 style={{color:THEME.primary, marginTop:0, fontSize:'1rem'}}>{sec.title}</h4>
                                {sec.questions.map(q => (
                                    <div key={q.id} style={{marginBottom:'15px'}}>
                                        <label style={{color:'white', fontSize:'0.9rem', display:'block', marginBottom:'8px'}}>{q.text}</label>
                                        <div style={{display:'flex', gap:'5px'}}>
                                            {['Yes', 'No', 'N/A'].map(opt => (
                                                <RadioPill key={opt} label={opt} selected={questionAnswers[q.id] === opt} onClick={() => handleQuestionAnswer(q.id, opt, q.linked_permit)} color={THEME.primary} />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                        
                        {/* Permits */}
                        <div style={{marginBottom:'20px', background:'#020617', padding:'15px', borderRadius:'8px', border:`1px solid ${THEME.border}`}}>
                            <h4 style={{color:THEME.warning, marginTop:0, display:'flex', alignItems:'center', gap:'8px'}}><ShieldCheck size={18}/> Required Permits</h4>
                            <div style={{display:'grid', gap:'10px'}}>
                                {availablePermits.map(type => {
                                    const isSel = !!permitData[type.id];
                                    return (
                                        <div key={type.id} style={{background: isSel ? 'rgba(250, 204, 21, 0.1)' : 'transparent', padding:'10px', borderRadius:'6px', border:`1px solid ${isSel ? THEME.warning : THEME.border}`}}>
                                            <div onClick={() => handleTogglePermit(type.id)} style={{display:'flex', alignItems:'center', cursor:'pointer'}}>
                                                <div style={{width:'20px', height:'20px', borderRadius:'4px', border:`2px solid ${isSel ? THEME.warning : THEME.textSec}`, display:'flex', alignItems:'center', justifyContent:'center', marginRight:'10px'}}>
                                                    {isSel && <div style={{width:'10px', height:'10px', background: THEME.warning, borderRadius:'2px'}} />}
                                                </div>
                                                <span style={{color: isSel ? 'white' : THEME.textSec, fontWeight:'bold'}}>{type.name}</span>
                                            </div>
                                            {isSel && (
                                                <div style={{marginTop:'10px', paddingLeft:'10px', borderLeft:`2px solid ${THEME.warning}44`}}>
                                                    {renderPermitQuestions(type)}
                                                    <input placeholder="Permit specific notes..." onChange={e => handlePermitNotesChange(type.id, e.target.value)} style={{...inputStyle, marginTop:'5px', fontSize:'0.8rem'}} />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <button type="submit" className="btn-primary" style={{width:'100%', padding:'15px', background: THEME.success, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', fontSize:'1.1rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px'}}><CheckCircle2 size={20}/> Submit Log</button>
                    </form>
                </div>
            )}

            {activeTab === 'history' && (
                <div style={{display:'grid', gap:'15px'}}>
                    {historyLogs.length === 0 ? <div style={{textAlign:'center', color:THEME.textSec, padding:'40px'}}>No log history found.</div> : historyLogs.map(log => (
                        <div key={log.id} style={{background: THEME.card, padding:'15px', borderRadius:'12px', border:`1px solid ${THEME.border}`}}>
                            <div style={{display:'flex', justifyContent:'space-between', color:'white', fontWeight:'bold'}}>
                                <span style={{display:'flex', alignItems:'center', gap:'6px'}}><Clock size={16} color={THEME.primary}/> {log.date}</span>
                                <span style={{color:THEME.primary}}>{log.project_name}</span>
                            </div>
                            <div style={{color:THEME.textSec, marginTop:'5px', fontSize:'0.9rem', whiteSpace:'pre-wrap'}}>{log.notes}</div>
                            {log.permits && log.permits.length > 0 && <div style={{display:'flex', gap:'5px', marginTop:'10px'}}>{log.permits.map(p => <span key={p.id} style={{fontSize:'0.75rem', padding:'2px 8px', borderRadius:'4px', background:'rgba(250, 204, 21, 0.1)', color:THEME.warning, border:`1px solid ${THEME.warning}44`}}>{p.permit_type_name} ({p.status})</span>)}</div>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// --- MINI MAP PICKER COMPONENT ---
const MapPicker = ({ onSelect }) => {
    const [bgImage, setBgImage] = useState(null);
    const [rows, setRows] = useState(10);
    const [cols, setCols] = useState(10);

    useEffect(() => {
        const loadMap = async () => {
            try {
                const res = await api.get('config/map-settings/');
                if (res.data) {
                    setRows(parseInt(res.data.rows) || 10); 
                    setCols(parseInt(res.data.cols) || 10);
                    if (res.data.image) setBgImage(res.data.image);
                }
            } catch (err) { console.error("Map fetch error", err); }
        };
        loadMap();
    }, []);

    const getRowLabel = (index) => {
        let label = '';
        let i = index;
        while (i >= 0) {
            label = String.fromCharCode((i % 26) + 65) + label;
            i = Math.floor(i / 26) - 1;
        }
        return label;
    };

    const rowsArray = Array.from({ length: rows }, (_, i) => getRowLabel(i));
    const colsArray = Array.from({ length: cols }, (_, i) => i + 1);

    return (
        <div style={{ position: 'relative', width: 'fit-content', minWidth: '100%', minHeight: '400px', background: '#020617' }}>
            {bgImage ? <img src={bgImage} alt="Map" style={{ display: 'block', maxWidth: '100%', minWidth: '100%', height: 'auto', opacity: 0.7 }} /> : <div style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign:'center', color:'#94a3b8'}}>No Map Uploaded</div>}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'grid', gridTemplateColumns: `30px repeat(${cols}, 1fr)`, gridTemplateRows: `30px repeat(${rows}, 1fr)` }}>
                <div style={{background:'rgba(0,0,0,0.6)', zIndex:2}}></div>
                {colsArray.map(c => <div key={`h${c}`} style={{background:'rgba(0,0,0,0.6)', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.7rem', fontWeight:'bold', borderBottom:'1px solid rgba(255,255,255,0.2)'}}>{c}</div>)}
                {rowsArray.map(row => (
                    <React.Fragment key={`r${row}`}>
                        <div style={{background:'rgba(0,0,0,0.6)', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.7rem', fontWeight:'bold', borderRight:'1px solid rgba(255,255,255,0.2)'}}>{row}</div>
                        {colsArray.map(col => (
                            <div key={`${row}${col}`} onClick={() => onSelect(`${row}${col}`)} style={{ border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={(e) => e.target.style.background = 'rgba(59, 130, 246, 0.4)'} onMouseLeave={(e) => e.target.style.background = 'transparent'} />
                        ))}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

// --- SEARCHABLE DROPDOWN COMPONENT ---
const SearchableDropdown = ({ options, value, onChange, placeholder, labelFn }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');

    const selected = options.find(o => o.id === parseInt(value));
    const filtered = options.filter(o => labelFn(o).toLowerCase().includes(search.toLowerCase()));

    return (
        <div style={{position:'relative'}}>
            {/* The "Box" */}
            <div onClick={() => setIsOpen(true)} style={{width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color: selected ? 'white' : '#94a3b8', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', boxSizing: 'border-box', marginBottom: '10px'}}>
                <span style={{whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{selected ? labelFn(selected) : placeholder}</span>
                <ChevronDown size={16}/>
            </div>

            {/* The Menu */}
            {isOpen && (
                <>
                    <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, zIndex:100}} onClick={() => setIsOpen(false)} />
                    <div style={{position:'absolute', top:'100%', left:0, right:0, zIndex:101, background:'#1e293b', border:`1px solid ${THEME.border}`, borderRadius:'6px', marginTop:'-5px', maxHeight:'250px', overflowY:'auto', boxShadow:'0 10px 15px -3px rgba(0, 0, 0, 0.5)'}}>
                        <div style={{position:'sticky', top:0, background:'#1e293b', padding:'8px', borderBottom:`1px solid ${THEME.border}`}}>
                            <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Type to filter..." style={{width:'100%', padding:'8px', borderRadius:'4px', background:'#0f172a', border:`1px solid ${THEME.border}`, color:'white', outline:'none', boxSizing:'border-box'}} />
                        </div>
                        {filtered.map(opt => (
                            <div key={opt.id} onClick={() => { onChange(opt.id); setIsOpen(false); setSearch(''); }} style={{padding:'10px', cursor:'pointer', borderBottom:'1px solid #334155', background: opt.id === parseInt(value) ? 'rgba(59, 130, 246, 0.2)' : 'transparent', color: opt.id === parseInt(value) ? THEME.primary : 'white', fontSize:'0.9rem'}} onMouseEnter={e => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)'} onMouseLeave={e => e.currentTarget.style.background = opt.id === parseInt(value) ? 'rgba(59, 130, 246, 0.2)' : 'transparent'}>
                                {labelFn(opt)}
                            </div>
                        ))}
                        {filtered.length === 0 && <div style={{padding:'15px', textAlign:'center', color:'#94a3b8', fontSize:'0.9rem'}}>No matches found.</div>}
                    </div>
                </>
            )}
        </div>
    );
};

export default ContractorLogManager;